import {Suspense, useEffect, useState} from "react";
import React from "react";
import {Portal} from "@mui/material";
import {useLocale, useMessages} from "i18n";
import {VisuallyHidden} from "components/Accessibility.styles";
import {useIsLoggedIn, useUser} from "../../UserProvider";
import {planOf} from "../gating";
import {offersRequestFiles} from "../requestFilesOffer";
import {composerModeAfterFiles, type ComposerMode} from "../request/composerMode";
import type {SenderChoiceControls} from "./useSenderChoice";
import {senderBlocker, sentAs} from "sender/senderChoice";
import type {RequestPageDraft} from "../personalization/personalizationPreview";
import {useComposerSlide} from "../request/useComposerSlide";
import {usePricingUpgrade} from "pricing/usePricingUpgrade";
import {PricingDialog} from "pricing/PricingDialog";
import {PlanChangeDialog} from "pricing/PlanChangeDialog";
import {FileSummary, SummaryFile} from "./FileSummary";
import type {FileListFacts} from "../overLimit/fileListRules";
import {AddHero} from "./AddHero";
import {ModeToggle} from "./ModeToggle";
import type {ReplyTarget} from "download/reply/replyPrompt";
import {OptionsSheet} from "./OptionsSheet";
import {PersonaliseSheet} from "./PersonaliseSheet";
import type {PersonalizationVM} from "../personalization/usePersonalizationDraft";
import {outgoingPersonalizationRung} from "../personalization/outgoingPersonalization";
import {draftSceneTemplate, type PersonalizationDraftError} from "../personalization/personalizationDraft";
import {effectivePersonalizationAccent, previewPersonalizationAccent} from "home/personalization/personalizationAccent";
import {useLoginModal} from "../../login/LoginModalProvider";
import {ToastContext} from "Toast";
import {BrandingIcon} from "nav/pro/icons";
import {useCreateForm, TransferPayload} from "./useCreateForm";
import {activeStorageLabel, planLiftingActiveStorage, planLiftingMonthlyDownloads} from "pricing/planEntitlements";
import {EmailField} from "./FormField";
import {Nudge} from "./UpgradeHints";
import {ActiveStorageNotice} from "./ActiveStorageNotice";
import {OverLimitNotice} from "./OverLimitNotice";
import {OverLimitCheckoutDialog} from "./OverLimitCheckoutDialog";
import {createPortal} from "react-dom";
import {DownloadLimitCard} from "./DownloadLimitCard";
import {PlanUpsellCard} from "./PlanUpsellCard";
import {composerRecipientCount, downloadLimitCard} from "./limitCard";
import {useSlideOutCards} from "./useSlideOutCards";
import {slideOutCardIsOut, slideOutCardsBesideOffer} from "./slideOutCards";
import {useLimitWallOffer} from "offers/limitWall/useLimitWallOffer";
import {LimitWallDialog} from "offers/limitWall/LimitWallDialog";
import {LimitWallOfferCard} from "offers/limitWall/LimitWallOfferCard";
import {LimitWallExpiredNotice} from "offers/limitWall/LimitWallExpiredNotice";
import {limitWallCard} from "offers/limitWall/limitWallOffer";
import {useUpsellCard} from "./useUpsellCard";
import {useUpgradeMoment} from "./useUpgradeMoment";
import {UpgradeMomentCard} from "./UpgradeMomentCard";
import {planCtaPrice, type PlanId} from "pricing/pricingPlans";
import type {PaidPlanId} from "pricing/planCatalogue";
import type {OfferHost} from "pricing/planPickerPress";
import {CompactUpgradeDialog} from "upsell/CompactUpgradeDialog";
import {upgradeDialogFor, type UpgradeEntryPoint} from "upsell/upgradeDialogChoice";
import {useLimitOffer} from "../../upsell/useLimitOffer";
import {useOfferEvents} from "../../upsell/useOfferEvents";
import {upsellCardDetail} from "../../upsell/upsellCardWall";
import type {CompactDialogEntry} from "upsell/offerEvents";
import {usePaidMoment} from "../../upsell/usePaidMoment";
import {offerFirstMonthFigures} from "../../upsell/firstMonthPricing";
import {formatMoney} from "pricing/PricingStorage";
import {FirstMonthPlansDialog} from "../../upsell/FirstMonthPlansDialog";
import {VerifyEmailCallout} from "../../emailVerification/VerifyEmailCallout";
import {OfferCallouts} from "../../upsell/OfferCallouts";
import {UnusedPurchasesNotice} from "./UnusedPurchasesNotice";
import type {BesideOfferHost} from "upsell/besideOfferHost";
import {overLimitCheckoutOnScreen, type OverLimitPricing} from "../overLimit/overLimitOffer";
import {useDownloadUsage} from "account/hooks/useDownloadUsage";
import {useResolvedStorefrontQuote} from "pricing/useQuotedPricing";
import {useReferralOffer} from "referral/useReferralOffer";
import {useReferralClaim} from "referral/useReferralClaim";
import {ReferralOfferDialog} from "referral/ReferralOfferDialog";
import {upgradeCtaKind} from "referral/referralOffer";
import {InfoIcon} from "./icons";
import {
	Body, ComposerStage, ControlPair, Field, Foot, ModeRow, Notices, OptionsButtonIcon, PairButton, PairButtonTag,
	RequestLayer, SendLayer,
	PanelControls, Root, SceneThemeLabel, SceneThemeRow, SceneThemeThumb, SendBlocked, SubmitButton,
} from "./TransferPanel.styles";
import {Switch} from "./OptionsSheet.styles";
import {GLYPH_MARK_COUNT, GlyphMark} from "./TemplateTilePicker.styles";

/**
 * Request mode's tab and composer (kitecorp/transferify-ui#3140), in a chunk of their own, fetched only once
 * {@link offersRequestFiles} says this sender gets them.
 *
 * Their own chunk keeps the landing's module order what the pre-rendered pages were built with. Importing
 * them statically moved modules between chunks, which changed the order the browser creates styled
 * components in, so their generated class names no longer matched the pre-rendered HTML: once the
 * recipient page's surface re-rendered, its rules stopped reaching the hero inside it and the file card
 * lost its height cap (download-acquisition-footer.spec.ts caught it). The underlying fragility is
 * filed separately; this keeps the change from triggering it.
 */
let requestModeModule: typeof import("../request/requestModeChunk") | null = null;
const loadRequestMode = () => import("../request/requestModeChunk").then(module => (requestModeModule = module));
const RequestModeTab = React.lazy(() => loadRequestMode().then(module => ({default: module.RequestModeTab})));
/**
 * The composer, taken straight from the chunk rather than lazily: it renders only once request mode has been
 * entered, which is only ever after the chunk arrived (the tab is the chunk's own, and a held ask waits for
 * it). A lazy component suspends on its own first render even for a module already loaded, and an empty
 * composer on that first slide gave the panel no height to slide to.
 */
const loadedRequestComposer = () => requestModeModule?.RequestComposer ?? null;

export type {TransferPayload};

interface Props {
	replyTarget?: ReplyTarget | null;
	replyActive?: boolean;
	/**
	 * The panel is OBSCURED and must not be operable, without unmounting its draft.
	 *
	 * Two callers set it, and the name is the older of the two: a destination CardSheet covering this
	 * persistent panel (ui#1061), and since ui#1549 the consent gate standing in front of it. Both mean
	 * the same thing here — everything inside {@link PanelControls} goes `inert` — and neither discards
	 * what the sender has composed. What differs is the visual treatment, which belongs to the surface
	 * doing the obscuring, never to this panel: since ui#1728 the gate is an opaque takeover that fills
	 * the card ({@link ../../terms/Consent.styles}), and a CardSheet paints its own. Either way this
	 * panel keeps its real box at its real size and is only COVERED — the ui#1549 rule that it must
	 * never be removed from the layout.
	 */
	covered: boolean;
	/**
	 * The Requested tab asked for request mode, and the create flow kept the ask until this panel showed
	 * (kitecorp/transferify-ui#3140). Spent here once the panel is uncovered and offers request mode.
	 */
	requestModeOwed: boolean;
	onRequestModeOwedSpent: () => void;
	/** A request was created with the look: forget the look as Send another does. */
	onRequestLookSpent: () => void;
	/**
	 * Whether a request may be made now for the requester's address `email`: true for an authenticated
	 * session; otherwise the create flow asks to confirm `email` with a code, exactly as a Send does, and
	 * this answers false (kitecorp/transferify-ui#3140, owner 2026-10-01).
	 */
	admitRequest: (email: string) => Promise<boolean>;
	/**
	 * Where the slide-out cards render (ui#2468, ui#2278): a host BESIDE this panel in the peel frame
	 * CreateFlow owns, or null before the frame exists. A card must be the panel's sibling — a descendant
	 * cannot paint behind its own ancestor — while its data (the usage, this draft's recipients, the wall
	 * just met, the one call to action) is this panel's, so the panel portals the cards into the host.
	 *
	 * ONE host for the whole family, not one per card: the two are mutually exclusive by rule, and the
	 * slot renders a single candidate — two candidates could never be on screen together at all.
	 */
	slideOutHost: HTMLElement | null;
	/** Where the trial-expiry offer hangs beside the card (ui#2949); CreateFlow's frame owns the place. */
	besideOfferHost: BesideOfferHost;
	/** Per-file view models (name/size/optional folder path, ui#94) — raw `File`s also satisfy it. */
	files: SummaryFile[];
	/**
	 * The files are ones the account already stores (kitecorp/transferify-ui#3405): the send makes a new link to
	 * them. The panel then offers a hosted upload's options only (no mode to pick, no Zero-Knowledge), no way to
	 * add files from this device, and a submit that says nothing about uploading.
	 */
	storedFiles?: boolean;
	/** The selection's file-list facts (kitecorp/transferify-ui#2930), measured by {@link ../CreateFlow}. */
	fileList: FileListFacts;
	onSubmit: (payload: TransferPayload) => void;
	onRemoveFile: (index: number) => void;
	isSeeding: boolean;
	/**
	 * A sign-in request or confirmation, or a Send's wait for the restored session (ui#2088), is in
	 * flight: the submit is disabled and reports busy until it settles.
	 */
	authInFlight: boolean;
	/**
	 * The e-mail code step {@link ../CreateFlow} owns is open. Read only by the signed-out over-limit counter
	 * (kitecorp/transferify-ui#3435), whose press is that step opening.
	 */
	signInStepOpen: boolean;
	/**
	 * The api refused the last submit because the account's LIVE transfers already fill the plan's
	 * active-storage cap (ui#1664). Owned by {@link ../CreateFlow}, which classifies the refusal by
	 * the api's stable `errorId` and clears this on the next submit; the panel only renders the
	 * {@link ActiveStorageNotice} strip for it.
	 */
	activeStorageRefused: boolean;
	/**
	 * The registration was refused for a plan, with the wall it was refused on: Instant
	 * (`error.transfer.instant.planRequired`, kitecorp/transferify-api-service#3005, Q5) or hosted
	 * (`error.transfer.free.maxUpload`, kitecorp/transferify-ui#3800), or null. Owned by {@link ../CreateFlow}, like
	 * {@link activeStorageRefused}, which clears it on the next submit; the form answers it by opening the sized plan
	 * dialog (one dialog for both walls) and calls {@link onPlanRefusalHandled} once it has.
	 */
	planRequiredRefused: OverLimitPricing | null;
	/** The plan dialog opened for that refusal, so the refusal is dropped and cannot open it again. */
	onPlanRefusalHandled: () => void;
	/**
	 * The personalization builder's state, owned by {@link ../CreateFlow} rather than by this panel
	 * (ui#1814).
	 *
	 * It has to outlive this component: the builder also opens itself over the PROGRESS card, and this
	 * panel is unmounted for the whole of that phase. A draft held here would be destroyed by the very
	 * transition that makes the builder useful.
	 */
	personalise: PersonalizationVM;
	/**
	 * Who the transfer or the request goes out as, a person or a company (owners 2026-10-01 and 2026-10-02), chosen in
	 * the Personalise builder beside "Your name" and read by whichever composer sends. Owned by {@link ../CreateFlow}
	 * for the same reason as the look: a send that is cancelled or fails comes back to a new panel, and its retry must
	 * still go out as the company the sender chose.
	 */
	senderChoice: SenderChoiceControls;
	/** The builder was raised from this panel; the flow records it and counts it. */
	onBuilderOpened: () => void;
	/** The sender left the builder KEEPING what they composed. */
	onBuilderCommitted: () => void;
}

/** The panel's one editor: a send's Options, a request's Options, or the personalization builder. */
type EditorKind = "options" | "requestOptions" | "personalise";
type EditorState = {kind: EditorKind; open: boolean} | null;

/** Sliders/adjustments glyph for the Options button (currentColor so it inherits the tile tint). */
function SlidersGlyph() {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
			 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<line x1="4" y1="21" x2="4" y2="14"/>
			<line x1="4" y1="10" x2="4" y2="3"/>
			<line x1="12" y1="21" x2="12" y2="12"/>
			<line x1="12" y1="8" x2="12" y2="3"/>
			<line x1="20" y1="21" x2="20" y2="16"/>
			<line x1="20" y1="12" x2="20" y2="3"/>
			<line x1="1" y1="14" x2="7" y2="14"/>
			<line x1="9" y1="8" x2="15" y2="8"/>
			<line x1="17" y1="16" x2="23" y2="16"/>
		</svg>
	);
}

/**
 * The v3 create-transfer card (ui#49, reflowed in ui#214, polished in ui#215, re-composed in ui#216).
 * Top → bottom: the framed {@link FileSummary} payload box (pinned usage header + the scrolling file
 * list), the borderless sender-email field, the [Instant | Upload] mode selector — ui#214's relocated
 * row, rendered by the rich {@link ModeToggle} radio-cards (ui#215 restored the icons/descriptions/
 * tooltips that label-only tabs had dropped) and filling the row's width — a full-width **Options**
 * button that opens the non-modal {@link OptionsSheet} CardSheet, and the submit foot.
 *
 * **Scroll model (ui#216):** the card is a capped-height flex column and the file list is its ONLY
 * scroller. Everything the panel renders outside the payload box sits in a `flex: none` {@link Body},
 * so the payload header, "Your email", the mode row, Options and Send all stay put while only the
 * files move. See {@link ./TransferPanel.styles} and {@link ./FileSummary.styles} for the contract.
 *
 * Title + Message left the panel in ui#216 — not everyone wants to type, and the fields did not earn
 * the space; they now live in the sheet with friendly editable defaults, and `composeMessage` still
 * folds them into the single API `message`. Every transfer option (delivery/recipients, password,
 * download limit, expiry) lives in the sheet too — the panel stays focused on the files, who it is
 * from, and how it goes. A dumb view: all state, gating and payload logic live in
 * {@link useCreateForm} (epic #26); only the drop hero shows before any file is picked.
 *
 * **Upgrade opens a dialog in place (ui#217, ui#2529).** Every Upgrade affordance in the create flow —
 * the sheet's footer button, the over-cap {@link Nudge}, the recipients field's inline nudge, the
 * slide-out upsell card and every PRO-locked control — opens a dialog HERE instead of routing to
 * `/pricing`. Navigating away unmounted {@link ../CreateFlow} and destroyed the in-progress transfer:
 * the picked files, the chosen options and anything typed. `goToPricing` still exists for surfaces
 * that legitimately navigate to the pricing page; the create flow just never uses it.
 *
 * WHICH dialog each control opens is `upsell/upgradeDialogChoice.upgradeDialogFor` — a React-free rule
 * with its own tests — and `openUpgrade` below is the one place that executes it. The single-plan
 * upsells raise {@link CompactUpgradeDialog}; the footer Upgrade, the two panel nudges and the
 * download-limit card keep the 3-plan {@link PricingDialog}. The branching behind a plan CTA
 * (anonymous → login modal, enterprise → billing portal, else → checkout) is {@link usePricingUpgrade}'s
 * and is not reimplemented here.
 */
export function TransferPanel({
	covered, files, storedFiles = false, fileList, onSubmit, onRemoveFile, isSeeding, authInFlight, signInStepOpen,
	activeStorageRefused, planRequiredRefused, onPlanRefusalHandled, slideOutHost, besideOfferHost,
	replyTarget = null, replyActive = false,
	personalise, senderChoice, onBuilderOpened, onBuilderCommitted, requestModeOwed, onRequestModeOwedSpent,
	onRequestLookSpent,
	admitRequest,
}: Props) {
	const [user] = useUser();
	const {sender, pricing, common, offerLadder, referralOffer: referralOfferCopy, account} = useMessages();
	const {locale} = useLocale();
	const {open: openLogin} = useLoginModal();
	const {showSuccessToast} = React.useContext(ToastContext);
	const plan = planOf(user);
	// Offered to every visitor, signed in or not, unless they are replying (owner 2026-10-01). The pages are
	// pre-rendered as a visitor who is not signed in, so the pre-rendered landing carries the tab, and hydration
	// keeps that markup until the tab's own chunk has arrived.
	const requestFilesOffered = offersRequestFiles({signedIn: useIsLoggedIn(), accountActive: user.isActive, replying: replyActive});
	// Which way the composer faces (kitecorp/transferify-ui#3140): sending files, or asking for them. Asking
	// is only ever on for a sender offered it, so losing the offer (a reply starting, or an account no longer
	// active) turns it back.
	const [composerMode, setComposerMode] = useState<ComposerMode>("send");
	const requestMode = requestFilesOffered && composerMode === "request";
	// The request's note and period as the composer reports them, for the builder's miniature of its upload page.
	const [requestPage, setRequestPage] = useState<RequestPageDraft | null>(null);
	// Where each composer is while the panel turns between them, and the panel's height across the turn.
	const composerSlide = useComposerSlide(requestMode ? "request" : "send");
	const RequestComposer = loadedRequestComposer();
	// How many times request mode was entered. The composer is mounted from the first entry on, and each
	// entry hands focus to it, since the tab that was pressed stays where it was.
	const [requestEntries, setRequestEntries] = useState(0);
	const enterRequestMode = React.useCallback(() => {
		setComposerMode("request");
		setRequestEntries(entries => entries + 1);
	}, []);
	const toggleComposerMode = () => {
		if (requestMode) setComposerMode("send");
		else enterRequestMode();
	};
	// Files picked or dropped while asking for files mean the sender is sending after all
	// (`composerModeAfterFiles`). Set while rendering, so the request composer never paints over them.
	const [seenFileCount, setSeenFileCount] = useState(files.length);
	if (files.length !== seenFileCount) {
		setSeenFileCount(files.length);
		setComposerMode(mode => composerModeAfterFiles(mode, seenFileCount, files.length));
	}
	// One identity owns the editor surface through its exit transition. Independent booleans let Options
	// and Personalise overlap, install competing Escape listeners, and race focus restoration (ui#1061).
	const [editor, setEditor] = React.useState<EditorState>(null);
	// Capture before `controlsInert` commits: applying inert may move activeElement to body before the
	// CardSheet mounts, but the connected opener becomes reachable again as soon as ordinary close begins.
	const editorReturnFocusRef = React.useRef<HTMLElement | null>(null);
	const [pricingOpen, setPricingOpen] = React.useState(false);
	// The tier subset the pricing dialog shows, when it was opened ON one plan (ui#2468); undefined is
	// the whole table, which every plain opener restores by going through `openPricing`.
	const [pricingPlanIds, setPricingPlanIds] = React.useState<PlanId[] | undefined>(undefined);
	// Whose funnel a checkout from that dialog belongs to (kitecorp/transferify-ui#2914): the download-cap
	// card or the storage strip that opened it, or null for the plan picker's own. Outlives the close for
	// the same reason the tier subset does.
	const [pricingHost, setPricingHost] = React.useState<OfferHost | null>(null);
	// Whether the download-limit card's plain route has the picker open on the lifting plan: the one
	// state in which that plan's price is the figure this moment reports as seen.
	const [limitPriceShown, setLimitPriceShown] = React.useState(false);
	// The COMPACT single-plan dialog (ui#2529), for the upsells that already know their tier.
	// The tier OUTLIVES the close, exactly as `pricingPlanIds` does: clearing it would unmount the
	// dialog mid-transition and take its exit animation and MUI's focus restore with it.
	// One value for the tier, the door that opened it (api#1799) and why, so none can be set apart: only a
	// request refused at the plan's cap says why (kitecorp/transferify-ui#3140); every other door, null.
	const [compact, setCompact] = React.useState<{plan: PaidPlanId; entry: CompactDialogEntry; reason: string | null} | null>(null);
	const [compactOpen, setCompactOpen] = React.useState(false);
	// A subscription was bought IN THIS TAB and Stripe's `invoice.paid` webhook has not landed yet, so
	// `/account` still reports the old tier and every wall on this panel is still armed. Held here, at
	// the one place that opens an upgrade surface, because that is the press this has to answer
	// (ui#2529) — without it the buyer is one click from a second subscription.
	//
	// IT DOES NOT RESET, and that is deliberate rather than an oversight. The condition it stands in
	// for ends when the webhook lands and the next `/account` read reports the paid tier — at which
	// point `plan` below is paid, no control on this panel is locked any more, and nothing asks. A
	// timer that re-armed selling while the same purchase was still settling would be the defect this
	// exists to prevent, in the one window where the api would still mint a second subscription.
	const [upgradeConfirming, setUpgradeConfirming] = React.useState(false);
	const optionsOpen = editor?.kind === "options" && editor.open && !covered;
	const requestOptionsOpen = editor?.kind === "requestOptions" && editor.open && !covered;
	const personaliseOpen = editor?.kind === "personalise" && editor.open && !covered;
	const controlsInert = covered || (editor?.open === true && !covered);
	/**
	 * Files arriving turn the composer to sending while the request Options are open. The sheet's opener
	 * then hides with the request composer, so the sheet's own return of focus cannot land; Send takes it
	 * once the sheet has gone instead of the page.
	 */
	const focusSendAfterEditor = React.useRef(false);
	const openEditor = React.useCallback((kind: EditorKind, returnFocus: HTMLElement) => {
		if (editorReturnFocusRef.current !== null) {
			return;
		}
		editorReturnFocusRef.current = returnFocus;
		setEditor({kind, open: true});
	}, []);
	const closeEditor = React.useCallback(() => {
		setEditor(current => current === null || !current.open ? current : {...current, open: false});
	}, []);
	const releaseEditor = React.useCallback(() => {
		editorReturnFocusRef.current = null;
		setEditor(null);
		if (focusSendAfterEditor.current) {
			focusSendAfterEditor.current = false;
			submitRef.current?.focus();
		}
	}, []);

	// Route navigation closes a transient editor immediately, but the stable CreateFlow and its view-model
	// stay mounted. `!covered` above removes the sheet in the route's first render; this state transition
	// prevents it reopening if the sender returns before the exit callback runs.
	React.useEffect(() => {
		if (covered) {
			closeEditor();
		}
	}, [covered, closeEditor]);
	// A request's Options belong to request mode. Files dropped on the page while they are open turn the
	// composer to sending, and the sheet closes with the mode it was about. Losing the offer (a reply starting,
	// or an account no longer active) unmounts the request composer and the sheet with it, and a sheet that is
	// gone never reports its exit, so the editor is released here instead: otherwise Options and Personalise
	// stay locked until a reload.
	React.useEffect(() => {
		if (editor?.kind !== "requestOptions") return;
		if (!requestFilesOffered) {
			releaseEditor();
		} else if (!requestMode && editor.open) {
			focusSendAfterEditor.current = true;
			closeEditor();
		}
	}, [requestFilesOffered, requestMode, editor?.kind, editor?.open, closeEditor, releaseEditor]);
	// The Requested tab's buttons ask for request mode and move the tab home; the create flow keeps the ask
	// until this panel shows (`requestModeOwed`). Spent here once the panel is uncovered and offers request
	// mode. Not before it offers it: a panel mounted afresh, as one is when a success card closes, renders
	// its first frame signed out, and spending the ask then would drop it.
	// It waits for the request chunk, which the tab is still fetching on a panel that has just mounted, so the
	// composer it opens is never rendered before its code has arrived.
	useEffect(() => {
		if (covered || !requestModeOwed || !requestFilesOffered) return;
		let current = true;
		void loadRequestMode().then(() => {
			if (!current) return;
			enterRequestMode();
			onRequestModeOwedSpent();
		});
		return () => {
			current = false;
		};
	}, [covered, requestModeOwed, requestFilesOffered, enterRequestMode, onRequestModeOwedSpent]);
	// The same signed-in upgrade flow the chrome mounts (ui#167, ProfileMenu): the hook owns every
	// branch behind a plan CTA and the checkout-dialog state — nothing about it is re-decided here. No
	// surface of its own (kitecorp/transferify-ui#2914): the pricing dialog's presses name theirs, and the
	// compact dialog takes the checkout verdict itself and hands only non-checkout verdicts back here.
	const {onUpgrade, checkout, closeCheckout, planChange} = usePricingUpgrade(null);
	// The ONE upgrade entry point for the whole create flow (ui#217, rerouted by ui#2529). Every control
	// on this panel that offers an upgrade names ITSELF here, and `upgradeDialogFor` — a React-free rule
	// with its own tests — answers which surface that control opens. Nothing below decides it: the
	// single-plan upsells and the ladder entries differ by product decision, not by whether the caller
	// happens to hold a tier.
	//
	// The OptionsSheet deliberately stays OPEN behind whichever dialog opens rather than closing first:
	// the user came from the options they were mid-way through setting, and returns to them. The nested
	// modal restores focus to its opener inside the still-mounted CardSheet, and CardSheet yields Escape
	// while that aria-modal layer is present, so only the top layer closes and the in-progress transfer
	// survives. Both dialogs are mounted at the Root level (below, siblings of the sheet).
	const openUpgrade = (entry: UpgradeEntryPoint, reason: string | null = null) => {
		// A purchase this tab has already made answers every further press with what actually happened,
		// rather than opening a surface that would take the money again.
		if (upgradeConfirming) {
			showSuccessToast(pricing.upgradeDialog.confirming);
			return;
		}
		const choice = upgradeDialogFor(entry);
		if (choice.dialog === "compact") {
			setCompact({plan: choice.plan, entry: choice.entry, reason});
			setCompactOpen(true);
			return;
		}
		setPricingPlanIds(choice.planIds ?? undefined);
		setPricingHost(choice.host);
		setPricingOpen(true);
	};
	// The plan press (the size wall's on either path, kitecorp/transferify-ui#3800): while a plan order placed in the
	// pay dialog is still being switched on, the api still quotes the plan rows, so a second press could place a
	// second subscription order. Answered with what happened instead, as every upgrade press above is.
	const startOverLimitPlanChoice = () => {
		if (upgradeConfirming) {
			showSuccessToast(pricing.upgradeDialog.confirming);
			return;
		}
		vm.overLimit.startPlanChoice();
	};
	// The over-cap nudge on the panel itself names no tier, so it keeps the whole ladder. The storage
	// strip's Upgrade names one since kitecorp/transferify-ui#3048; see `pressStorageUpgrade`.
	const openPricing = () => openUpgrade({entry: "createFlowNudge"});
	// The download-limit card's plain route opens the LADDER on the plan that lifts the cap (ui#2468):
	// a contextual entry like Plus → Premium, so a free sender is offered Basic, not the whole table.
	// It deliberately did NOT move to the compact dialog — the owner's 2026-09-17 list names three
	// surfaces and this is not one of them. It also arms that moment's PRICE_SEEN, which is why it is
	// its own route and not the upsell card's: the card's press is not the cap being met, and counting
	// it there would credit the cap moment with a figure no capped sender was shown.
	const openLimitPricingOn = (planId: PaidPlanId | null) => {
		setLimitPriceShown(true);
		openUpgrade({entry: "downloadLimitCard", target: planId, occasion: limitOffer.occasion});
	};
	// The upsell card's wall (kitecorp/transferify-ui#2278). Declared before the form because the form
	// REPORTS the walls it meets into it, and armed only while this sender is outside the quiet window —
	// which since ui#2555 is the ACCOUNT's window, asked of the api, with the browser record as the
	// fallback. `isLoggedIn` rather than the id: an anonymous session has an id but no account, and the
	// api answers 401 for one.
	const upsell = useUpsellCard(plan, user.id, user.isLoggedIn);
	const vm = useCreateForm({
		plan, files, fileList, isSeeding, authInFlight, signInStepOpen, onWall: upsell.arm, replyTarget, replyActive,
		// Which press opened the code step: the request composer's opens the same one (kitecorp/transferify-ui#3435).
		facing: requestMode ? "request" : "send",
		// A locked option row or expiry rung, sold on the tier that unlocks THAT control — the same
		// tier its own gold pill names.
		onUpgrade: target => openUpgrade({entry: "lockedControl", target}),
		// Sent as a company with no name, or a tax ID the api would refuse (kitecorp/transferify-ui#3186): Send says so.
		senderBlock: senderBlocker(senderChoice.sender),
		// The storage refusal arms the same over-limit purchase read, so a held pass still pays (kitecorp/transferify-ui#3046).
		storageRefused: activeStorageRefused,
		planRequiredRefusal: planRequiredRefused,
		onPlanRefusalHandled,
		storedFiles,
	});
	// The limit-wall email's link (kitecorp/transferify-ui#3197): the api redirects it to this screen with
	// its token, and the offer becomes the first card of the slide-out family below. The wall's mode is
	// preselected once per arrival, and only for a sender the card is for (a paid subscriber following the
	// link is offered nothing and keeps their mode): keyed on the mode the api stated, so the sender's own
	// later switch holds.
	const limitWall = useLimitWallOffer();
	const arrivalMode = limitWall.cardDue ? limitWall.arrival?.mode ?? null : null;
	const {setMode} = vm;
	useEffect(() => {
		if (arrivalMode !== null) setMode(arrivalMode);
	}, [arrivalMode, setMode]);
	// Where the over-limit payment dialog hands focus back. The dialog's opener — the strip's own plan
	// press — is unmounted by the time a PLACED order closes it, so the strip that replaces it takes focus
	// instead; see OverLimitCheckoutDialog. Only the size wall's strip opens it since
	// kitecorp/transferify-ui#3916: the storage strip sells no pass.
	const overLimitNoticeRef = React.useRef<HTMLDivElement | null>(null);
	const hasFiles = files.length > 0;
	// Monthly download usage (ui#309): fetched only while logged in; nothing to meter below 80%.
	const downloadUsage = useDownloadUsage(user.isLoggedIn);
	// The download-limit card's model (ui#2468): the usage level plus the recipients on THIS draft,
	// which only the composer knows. Null is the ordinary state and renders no card at all.
	const limitCard = downloadLimitCard(downloadUsage, composerRecipientCount(vm.recipients, vm.selectedGroups));
	// The offer at a HIT cap (kitecorp/transferify-ui#1450). The trigger is the usage level itself, so
	// no limit rule is restated here.
	const limitOffer = useLimitOffer(downloadUsage?.level === "reached");
	// The upgrade moments (kitecorp/transferify-ui#2909): the same upsell slot, armed by what this sender
	// has been doing rather than by a control they touched. A wall the sender meets takes the slot from a
	// moment, because the card then names what they just clicked (owner 2026-09-17); the moment has had
	// its showing either way.
	const upgradeMoment = useUpgradeMoment({
		plan, signedIn: user.isLoggedIn, accountId: user.id ?? null,
		selection: {files: files.length, overLimit: vm.overCap},
	});
	const momentModel = upsell.model === null ? upgradeMoment.model : null;
	const [claimingLimitOffer, setClaimingLimitOffer] = useState(false);
	// The plan the download-limit card's plain route names and opens the picker on (ui#2468): the
	// smallest plan that lifts THIS cap, the same answer the refusal row and the notice email give.
	const liftPlan = planLiftingMonthlyDownloads(plan);
	// The limit-wall offer card (kitecorp/transferify-ui#3197) follows the selection, so its title can say
	// the plan covers the files once they are back: the total is the selection's own, null while empty.
	const selectionBytes = files.length === 0 ? null : files.reduce((sum, file) => sum + file.size, 0);
	const offerCard = limitWallCard(limitWall.presentation, selectionBytes);
	const offerPlan = limitWall.cardDue && offerCard !== null ? offerCard.plan : null;
	// While the offer is active, a card pitching the SAME plan steps aside and hands the offer its reason
	// line (owner 2026-10-03); a different plan's card stacks under. One React-free rule decides all three
	// cards' eligibility, rather than three conditions spread over the portals below.
	const beside = slideOutCardsBesideOffer(offerPlan,
		{due: limitCard !== null, plan: liftPlan,
			reason: limitCard === null ? null : sender.limitCard.usedLine(limitCard.used, limitCard.limit)},
		{due: upsell.model !== null || momentModel !== null,
			// The sending-days moment renders no card of its own, so it names no plan to compare.
			plan: upsell.model?.target ?? (momentModel !== null && momentModel.moment !== "sendingDays" ? momentModel.target : null)});
	// The card's open/tucked state lives HERE, above the portalled card (ui#2468, `LimitCardOpenState`).
	// Shown, for the funnel, is the card OPEN — slid out, its content readable — since the tucked strip
	// shows no offer. Reported on EVERY branch of the call to action, not only the discounted one, so
	// the moment has a denominator in front of its claims.
	// ONE piece of state for the whole family, so no two cards can slide out over each other (ui#2278). The
	// showing a family records is the card OPEN — a tucked chip carries no offer to have been shown.
	const slideOutCards = useSlideOutCards({
		offer: offerPlan !== null,
		limit: beside.limit,
		upsell: beside.upsell,
		onOpened: id => {
			if (id !== "upsell") return;
			if (upsell.model !== null) upsell.rememberShown();
			else upgradeMoment.rememberShown();
		},
	});
	const limitCardControls = slideOutCards.limit;
	const limitCardShown = limitCard !== null && limitCardControls.open;
	// Held as a const so the render below narrows it once: a property read cannot stay narrowed inside the
	// card's own handler, and an assertion there would be a claim the compiler had already been denied.
	const upsellModel = upsell.model;
	// The funnel seam for the cap moment (api#999).
	const {reportClaimed: reportLimitOfferClaimed} = useOfferEvents(
		"DOWNLOAD_CAP_REACHED", limitCardShown, limitOffer.occasion);
	// ...and for the upsell card (ui#2555, api#1777). SHOWN is the card OPEN, exactly as the limit card's
	// is: a tucked chip carries no offer to have been shown. The WALL travels on both steps — the api
	// requires it on this moment, and the tier the card sells rides with it (api#1794). `upsellCardDetail`
	// is the whole of the translation, so this seam carries no vocabulary of its own. The card is armed for guests too, and the beacon routes their
	// appearance to the anonymous endpoint, which admits this moment's two appearance steps.
	// A moment reports under the same card with a wall of its own (api#2169), so its test group's surface
	// is on the funnel before any checkout. No wall, no report: the api refuses the card without one.
	const upsellDetail = upsellCardDetail(upsellModel, momentModel);
	const {reportClaimed: reportUpsellCardClaimed} = useOfferEvents(
		"UPSELL_CARD", upsellDetail !== null && slideOutCards.upsell.open, null, upsellDetail);
	// The rung of what is GOING OUT, not of what was composed: a draft the sender dismissed carries nothing, and
	// the rung rides with the request to the success screen (`outgoingPersonalizationRung`).
	const outgoingRung = outgoingPersonalizationRung(personalise.draft.enabled, personalise.rung);
	// The referred friend's gift (kitecorp/transferify-ui#1993, api#1228). ONE picker for the whole
	// panel: the notices strip, the download-cap wall and the active-storage refusal all open this
	// dialog, so a reader who meets the offer twice meets the same one.
	//
	// The storefront is fetched only for an account that HAS an offer — `useResolvedStorefrontQuote`
	// falls back to the baked catalogue otherwise, which is what every anonymous visitor already reads,
	// so the panel costs no extra request for the readers this never concerns.
	// ...and for the upsell card, whose line QUOTES a price: the card must name the figure the picker it
	// opens will charge, which on a geographic storefront is the quote rather than the baked catalogue.
	// The recipients field's own inline nudge (ui#2524) quotes the same kind of figure and is armed here
	// for the same reason — without it a sender whose only offer is that button would read the baked
	// price and then meet a different one in the dialog it opens.
	// ...and for the download-cap button, whose discounted label quotes the first month and whose
	// accessible name quotes the renewal price (ui#2545), both from this same storefront.
	const {storefront: referralStorefront} = useResolvedStorefrontQuote(
		user.referralOffer !== null || upsell.model !== null || momentModel !== null || vm.recip.upgrade !== null
			|| limitOffer.offer !== null);
	// ONE formatter for every monthly figure this panel quotes — the upsell card's line and the
	// recipients field's inline nudge. Both name the price the dialog they open will charge, so both
	// read the same resolved storefront through the same call rather than formatting it twice.
	const monthlyPriceOf = (planId: PaidPlanId) =>
		formatMoney(referralStorefront.pricing.currency, referralStorefront.pricing[planId].monthly, locale);
	const referralOffer = useReferralOffer(referralStorefront);
	const referralClaim = useReferralClaim();
	// WHICH of the two offers the single CTA carries is a React-free decision (`upgradeCtaKind`): a
	// reader can hold an unclaimed gift and be inside the cap-discount window at the same time, and the
	// surface has room for exactly one.
	// The button has room for the first-month amount alone, so the verb and the renewal price ride in its
	// accessible name (ui#2545); both figures come from one React-free pricing of the offered tier, and an
	// offer that cannot be priced honestly falls through to the plain upgrade route rather than to a label
	// with no figure.
	const capFigures = limitOffer.offer === null || limitOffer.plan === null
		? null
		: offerFirstMonthFigures(referralStorefront.pricing, limitOffer.plan, limitOffer.offer.percentOff, locale, pricing.welcome.thenMonthly);
	const capDiscountLabel = capFigures === null ? null : sender.limitCard.upgradeWithDiscount(capFigures.amount);
	const capCta = upgradeCtaKind({referralAvailable: referralOffer.available, discountLabel: capDiscountLabel});
	// The active-storage refusal asks the same rule. It has no discount of its own, so `discountLabel` is
	// null here rather than the cap's: that offer is the download meter's and belongs to that surface.
	const storageCta = upgradeCtaKind({referralAvailable: referralOffer.available, discountLabel: null});
	/*
	 * THE ACTIVE-STORAGE REFUSAL ON THE FUNNEL (kitecorp/transferify-ui#2664). Its moment is reported from
	 * `useCreateForm` since the strip gained a price (kitecorp/transferify-ui#3046): the pass is the same
	 * over-limit purchase the form already holds, so its figure, its checkout and its payment are reported
	 * where that purchase lives, and one instance keeps the strip's showing from being counted twice.
	 *
	 * The Upgrade press is counted wherever it then goes: the gift picker for a reader holding one, the
	 * compact dialog on the cheapest tier that lifts the storage cap for everyone else
	 * (kitecorp/transferify-ui#3048). Which surface it opens is the next question and each of those counts
	 * its own steps, the same rule `usePricingPage.pressPlanCta` follows.
	 */
	const pressStorageUpgrade = () => {
		vm.activeStorage.reportUpgradeClicked();
		if (storageCta === "referral-claim") referralOffer.show();
		else openUpgrade({entry: "activeStorageStrip", target: planLiftingActiveStorage(plan)});
	};
	// PRICE_SEEN for the download-limit card's plain route: the figure the reader meets is the lifting plan's monthly price on
	// the picker this card opened, reported under THIS moment rather than the picker's own so the funnel
	// reads shown → price → claim on one row. The discounted branch's dialog already reports its own
	// figure under the same moment, and the friend's gift reports REFERRAL_TRIAL from its picker.
	usePaidMoment("DOWNLOAD_CAP_REACHED", {
		visible: limitCardShown,
		price: limitPriceShown && liftPlan !== null ? planCtaPrice(referralStorefront.pricing, liftPlan, "MONTHLY") : null,
		occasion: limitOffer.occasion,
		reportsShown: false,
	});

	// Whether the sender has pressed Send at least once. Held as an ATTEMPT rather than as a stored
	// message so the alert below clears itself the instant the draft becomes sendable again — a captured
	// copy of `personalise.error` would still be on screen after the sender had already fixed it.
	const [sendAttempted, setSendAttempted] = React.useState(false);
	// The panel's single alert region (the ui#334 pattern): the producer hands over a catalog KEY and one
	// renderer resolves it, so this reason can never reach the sender in the wrong language.
	//
	// A DRAFT error and nothing else since ui#1814: every plan includes the look (kitecorp/transferify#525), so
	// the only reason a composed look cannot go out is the draft itself.
	const sendBlockedReason: PersonalizationDraftError | null =
		sendAttempted && personalise.blocksSend ? personalise.error : null;

	/** Send. */
	const submitRef = React.useRef<HTMLButtonElement>(null);

	/**
	 * The payload, assembled at the ONE place it is assembled.
	 *
	 * What may be folded in is `personalise.request`, which the domain returns as null for anything the
	 * api would refuse, so an unsendable draft can never ride along unnoticed. Every plan includes the look
	 * (kitecorp/transferify#525), so it goes out as composed.
	 */
	const send = () => {
		// Sent as a company (kitecorp/transferify-ui#3186), the transfer is named by the company, so the look's name,
		// typed under the person choice, stays home (`sentAs`, the request composer's rule too).
		const {company: senderCompany, look: request} = sentAs(senderChoice.sender, personalise.request);
		// The RUNG rides with the request (ui#2411), so what the success card is told was attached is what went out.
		onSubmit({
			...vm.buildPayload(),
			...(request ? {personalization: {request, rung: outgoingRung}} : {}),
			...(senderCompany === null ? {} : {senderCompany}),
		});
	};

	const submit = () => {
		if (vm.submit.disabled) return;
		setSendAttempted(true);
		// ui#763: an armed-but-unfinished builder must ANSWER the press, never swallow it. The submit
		// button is pressed with the sheet CLOSED, so the sheet's own error line — the only thing that
		// used to explain this — is not mounted; returning silently here is what made Send look dead.
		if (personalise.blocksSend) return;
		send();
	};

	/**
	 * Opens the composer for EVERYONE, signed in or not (ui#764).
	 *
	 * It used to divert a signed-out visitor straight to the login modal, which hid the feature behind
	 * a password field: nobody could see what personalization was, or that every plan includes it, without
	 * first having an account. The sheet now opens in a locked preview instead — browsable, saying it is
	 * included on every plan, and carrying its own sign-in call to action. Composing still needs an
	 * account (api#519 keeps every personalization endpoint authenticated), and every control inside
	 * the preview says so by being inert.
	 */
	const openPersonalise = (event: React.MouseEvent<HTMLButtonElement>) => {
		openEditor("personalise", event.currentTarget);
		onBuilderOpened();
	};

	// What the Personalise opener says about this transfer, or this request, without being opened: only that it
	// is ON. The button names the action; the sheet's own chip says the look is free.
	const personaliseTag = (optionsKind: "options" | "requestOptions") => {
		if (!personalise.draft.enabled) return null;
		return optionsKind === "requestOptions"
			? sender.personalise.forRequest.origin.overridden
			: sender.personalise.origin.overridden;
	};

	const effectiveMode = vm.hostedUpload ? "upload" : "instant";

	const senderAccent = previewPersonalizationAccent(effectivePersonalizationAccent(personalise.draft, personalise.inherited));

	/*
	 * Signed-out senders must type an address here (ui#71), and a visitor asking for files the same one in
	 * the same field, since they confirm it the same way (kitecorp/transferify-ui#3140). A signed-in
	 * sender's identity is canonical and read-only, so the row moves to the Options sheets instead of
	 * spending pinned panel space on a field nobody can edit (ui#853).
	 */
	const senderEmailField = vm.senderEmailLocked ? null : (
		<Field>
			<EmailField
				value={vm.senderEmail}
				onChange={vm.setSenderEmail}
				placeholder={sender.senderEmail}
				tip={sender.senderEmailTip}
				infoIcon={<InfoIcon/>}
			/>
		</Field>
	);

	/*
	 * The [Options | Personalise] pair (ui#556), in both directions of the composer: a send's Options or a
	 * request's, beside the one builder. Two openers, not a tab strip: each raises its own sheet, so
	 * `aria-haspopup="dialog"` is the honest role and neither is ever the "selected" one. Only the sibling
	 * opener is disabled while an editor is open: non-modal navigation stays live, but two fixed CardSheets
	 * can never overlap or race their Escape/focus-restoration handlers (ui#1061).
	 */
	const openerPair = (optionsKind: "options" | "requestOptions") => (
		<ControlPair>
			<PairButton
				type="button"
				onClick={event => openEditor(optionsKind, event.currentTarget)}
				disabled={editor?.kind === "personalise"}
				aria-haspopup="dialog"
				aria-expanded={optionsKind === "options" ? optionsOpen : requestOptionsOpen}
			>
				<OptionsButtonIcon><SlidersGlyph/></OptionsButtonIcon>
				{sender.optionsButton}
			</PairButton>
			<PairButton
				type="button"
				onClick={openPersonalise}
				disabled={editor !== null && editor.kind !== "personalise"}
				aria-haspopup="dialog"
				aria-expanded={personaliseOpen}
			>
				<OptionsButtonIcon><BrandingIcon size={16}/></OptionsButtonIcon>
				{sender.personaliseButton}
				{/* A GLYPH, not the sentence (ui#813): "Changed for this transfer" overflowed the pill in both
				    locales — the same disease the price tag had (ui#784). The full wording still reaches AT
				    through the hidden span. */}
				{personaliseTag(optionsKind) !== null && (
					<>
						<PairButtonTag aria-hidden>✓</PairButtonTag>
						<VisuallyHidden>{personaliseTag(optionsKind)}</VisuallyHidden>
					</>
				)}
			</PairButton>
		</ControlPair>
	);

	// The scene a Halloween slide or a `?tema=` link put on the draft (kitecorp/transferify-ui#3776): its own row
	// under the opener pair, with the off switch. A look chosen in the builder stays the opener's tick, as before.
	const sceneTheme = draftSceneTemplate(personalise.draft);
	const sceneThemeRow = sceneTheme === null ? null : (
		<SceneThemeRow>
			<SceneThemeThumb $template={sceneTheme} aria-hidden="true">
				{Array.from({length: GLYPH_MARK_COUNT[sceneTheme]}, (_unused, index) => <GlyphMark key={index}/>)}
			</SceneThemeThumb>
			<SceneThemeLabel>{sender.sceneTheme.on(account.theme.templates[sceneTheme].name)}</SceneThemeLabel>
			<Switch
				type="button"
				role="switch"
				$on
				aria-checked
				aria-label={sender.sceneTheme.turnOff(account.theme.templates[sceneTheme].name)}
				onClick={personalise.clearTemplate}
			>
				<span/>
			</Switch>
		</SceneThemeRow>
	);
	const composerControls = (optionsKind: "options" | "requestOptions") => (
		<>
			{openerPair(optionsKind)}
			{sceneThemeRow}
		</>
	);

	return (
		<Root $accent={senderAccent}>
			{/* Keep only the obscured sender controls out of the keyboard/accessibility graph. Editors and
			    nested modals are siblings below; the global navigation is outside this panel altogether. */}
			<PanelControls inert={controlsInert}>
				{/* The tab on the card's top edge that turns the composer between sending and asking for
				    files: first, so it is also first in the keyboard order, as it is in what the eye meets. */}
				{requestFilesOffered && (
					<Suspense fallback={null}>
						<RequestModeTab mode={requestMode ? "request" : "send"} onToggle={toggleComposerMode}/>
					</Suspense>
				)}
				{/* The unverified-address callout (ui#1458), on the surface every signed-in user sees.
				    Inside PanelControls with its siblings, so it goes inert with them when a destination
				    sheet covers the panel rather than staying reachable behind one. Notices owns the
				    spacing between them and below them; all three render null most of the time, and it
				    collapses when they do.

				    The unused-purchase note (ui#3004) leads: it is about money the reader has already
				    spent, and its "Use it" is the reason a returning buyer is on this panel at all. It is
				    NOT a subscription pitch, so it does not go through OfferCallouts' suppression: the
				    over-limit checkout may show beside a note about a purchase the reader already holds. */}
				<Notices>
					{/* A dead limit-wall link (kitecorp/transferify-ui#3197): one line, and the composer under it still sends. */}
					{limitWall.presentation.kind === "expired" && <LimitWallExpiredNotice/>}
					<UnusedPurchasesNotice/>
					<VerifyEmailCallout/>
					{/* Suppressed while the over-limit checkout is on screen (ui#1597): ui#1454 allows
					    no subscription pitch to compete with that purchase, and the composition is where
					    it was being broken. */}
					<OfferCallouts
						suppressed={overLimitCheckoutOnScreen(vm.overLimit.resolution)}
						referral={referralOffer}
						beside={{...besideOfferHost, yielding: slideOutCardIsOut(slideOutCards)}}
					/>
				</Notices>
				{/* The two composers' shared box: request mode slides in over the send composer from the right
				    and back out to it (kitecorp/transferify-ui#3140, `useComposerSlide`). */}
				<ComposerStage ref={composerSlide.stageRef} $sliding={composerSlide.sliding}>
					{/* Request mode, mounted from its first entry on and hidden while the composer sends files, so
					    a draft survives the turn there and back. */}
					{requestFilesOffered && requestEntries > 0 && RequestComposer !== null && (
						<RequestLayer
							$placement={composerSlide.placement.request}
							inert={composerSlide.placement.request === "slidingOut"}
						>
							<RequestComposer
								active={requestMode}
								entry={requestEntries}
								plan={plan}
								senderEmail={vm.senderEmail}
								senderEmailLocked={vm.senderEmailLocked}
								sender={senderChoice.sender}
								emailField={senderEmailField}
								admit={() => admitRequest(vm.senderEmail)}
								authInFlight={authInFlight}
								hostedLimitLabel={vm.hostedLimitLabel}
								canUpgrade={vm.recipientCanUpgrade}
								controls={composerControls("requestOptions")}
								look={{
									request: personalise.request,
									blockedReason: personalise.blocksSend && personalise.error !== null
										? sender.personalise.errors[personalise.error] : null,
								}}
								onLookSpent={onRequestLookSpent}
								optionsEditor={{
									open: requestOptionsOpen,
									onClose: closeEditor,
									onExited: releaseEditor,
									restoreFocusOnClose: !covered,
									returnFocus: editorReturnFocusRef.current,
								}}
								/* The same three doors as the send Options sheet: the footer's whole ladder, the
								   recipients nudge's single tier (ui#2524), and a locked control's tier, which the
								   longer "Open for" choices sell as a locked transfer expiry does. */
								onUpgrade={() => openUpgrade({entry: "optionsFooter"})}
								onUpgradeTo={target => openUpgrade({entry: "recipientsNudge", target})}
								onUnlock={target => openUpgrade({entry: "lockedControl", target})}
								onLimitReached={(target, reason) => openUpgrade({entry: "requestLimit", target}, reason)}
								onPageDraft={setRequestPage}
								monthlyPriceOf={monthlyPriceOf}
							/>
						</RequestLayer>
					)}
					{composerSlide.placement.send !== "hidden" && (
						<SendLayer $placement={composerSlide.placement.send} inert={composerSlide.placement.send === "beneath"}>
							{hasFiles ? (
								<>
									{/* The payload box: pinned usage header + the card's ONE scroll region (the files). */}
									<FileSummary files={files} mode={effectiveMode} plan={plan} onRemove={onRemoveFile} canAdd={!storedFiles}/>

									{/* Everything below is pinned — it must never travel with the file list (ui#216). */}
									<Body>
										{senderEmailField}

										<ModeRow>
											{!replyActive && !storedFiles && <ModeToggle value={vm.mode} onChange={vm.setMode}/>}
										</ModeRow>

										{composerControls("options")}

										{sendBlockedReason !== null && (
											<SendBlocked role="alert">
												{sender.personalise.errors[sendBlockedReason]}
											</SendBlocked>
										)}

										{/* The PAID tiers' answer within the largest plan: an upgrade nudge, unchanged. Above the
										    largest plan no upgrade can hold the send, so the strip below answers every tier
										    (kitecorp/transferify-api-service#3005). `overCapMessage` is null for a FREE sender
										    since ui#1454, so this and the priced path below are mutually exclusive by
										    construction rather than by a condition written twice. */}
										{vm.overCapMessage !== null && (
											<Nudge onUpgrade={openPricing}>
												{vm.overCapMessage}
											</Nudge>
										)}

										{/* The FREE tier's answer: the over-cap wall (ui#1454), which carries exactly one
										    CTA and is deliberately NOT wired to `openPricing`: no subscription pitch competes
										    with its plan press. The same strip also states the refusal above the largest plan,
										    for every tier, with no control.

										    A signed-out sender sees none of it (kitecorp/transferify-ui#3435): the usual
										    Send opens the e-mail code step, and the quote arms itself off `accountEmail`
										    the moment that sign-in lands, so the wall appears here in place. */}
										<OverLimitNotice
											rootRef={overLimitNoticeRef}
											resolution={vm.overLimit.resolution}
											pricing={vm.overLimit.pricing}
											confirming={vm.overLimit.confirming}
											onRetry={vm.overLimit.retryQuote}
											onChoosePlan={startOverLimitPlanChoice}
										/>

										{/* The api's account-level refusal (ui#1664): the plan's active-storage cap
										    is already spent by LIVE transfers, so unlike the per-transfer notices
										    above no change to THIS selection can clear it. Its exits are deleting an
										    old transfer and upgrading; a pass held from before the one-off stopped
										    being sold still pays for this send (kitecorp/transferify-ui#3046), which is
										    `storagePassPaid`'s answer, carried on `vm.activeStorage`. */}
										{activeStorageRefused && (
											<ActiveStorageNotice
												capLabel={activeStorageLabel(plan, pricing.compare.unlimited, locale)}
												passPaid={vm.activeStorage.passPaid}
												// Through `upgradeCtaKind` like the download-cap wall, not an inline
												// condition: that rule's own doc names this strip, and a rule stated
												// in one place and applied in another stops being either. This
												// surface carries no discount today, so it can only answer
												// "referral-claim" or "upgrade" — which is the point, because the
												// day it does carry one the answer comes from the same place.
												onUpgrade={pressStorageUpgrade}
												ctaLabel={storageCta === "referral-claim" ? referralOfferCopy.upgradeCta : null}
											/>
										)}
									</Body>
									<Foot>
										{/* The main button keeps its own label with a branded look riding: every plan
											includes the look (kitecorp/transferify#525), so the press sends it. */}
										<SubmitButton ref={submitRef} type="button" disabled={vm.submit.disabled}
											aria-busy={authInFlight} onClick={submit}>
											{vm.submit.label}
										</SubmitButton>
									</Foot>
								</>
							) : (
								<Body data-slide-out-anchor="">
									{/* On the limit-wall arrival the empty area asks for the files back (ui#3197). */}
									<AddHero hint={limitWall.cardDue ? offerLadder.limitWall.addFilesHint : undefined}/>
								</Body>
							)}
						</SendLayer>
					)}
				</ComposerStage>
			</PanelControls>

			{/* The limit-wall email's offer card (kitecorp/transferify-ui#3197): the first of the family, in
			    the same host. Its button opens the coupon checkout over the composer, so the selection the
			    sender rebuilt survives the payment; the api witnesses the opened checkout and the payment
			    itself, so no funnel step is reported from here. */}
			{slideOutHost !== null && offerPlan !== null && createPortal(
				<LimitWallOfferCard
					model={offerCard}
					controls={slideOutCards.offer}
					lead={beside.offerLead}
					onAction={limitWall.request}
				/>,
				slideOutHost,
			)}

			{/* The download-limit card (ui#2468) lives BESIDE the panel, in the peel frame CreateFlow owns,
			    and is portalled into the host that frame hands down — see DownloadLimitCard for why it
			    cannot be a child. The data is this panel's. The card keeps exactly ONE call to action, and
			    WHICH offer it carries is `upgradeCtaKind`'s answer, exactly as the banner it replaced. It
			    steps aside while the limit-wall offer sells the same plan (`beside`). */}
			{slideOutHost !== null && createPortal(
				<DownloadLimitCard
					model={beside.limit ? limitCard : null}
					controls={limitCardControls}
					// The claim is reported only on the DISCOUNTED branch (api#999): the plain upgrade
					// route is not this offer being taken, and counting it would credit the moment with
					// presses that had no offer behind them. The friend's gift reports its own
					// REFERRAL_TRIAL moment from the picker, so it must not report this one either.
					onAction={capCta === "referral-claim" ? referralOffer.show
						: capCta === "upgrade" ? () => openLimitPricingOn(liftPlan)
						: () => {
							reportLimitOfferClaimed();
							setClaimingLimitOffer(true);
						}}
					ctaLabel={capCta === "referral-claim" ? referralOfferCopy.upgradeCta
						: capCta === "discount" ? capDiscountLabel ?? common.upgrade
						: liftPlan === null ? common.upgrade : sender.limitCard.upgradeTo(pricing.planNames[liftPlan])}
					ctaAria={capCta === "discount" && capFigures !== null
						? sender.limitCard.upgradeWithDiscountAria(capFigures.amount, capFigures.note) : undefined}
				/>,
				slideOutHost,
			)}

			{/* The upsell card (ui#2278) shares that host and that stacking context: the two are ONE family
			    and only one of them is ever out, which is a rule about the pair and so cannot be expressed
			    by two independently-mounted tags. Its button opens the COMPACT dialog on the tier the card
			    names (ui#2529) — the card has already made the choice a three-tier table would re-ask. */}
			{slideOutHost !== null && beside.upsell && upsellModel !== null && createPortal(
				<PlanUpsellCard
					model={upsellModel}
					controls={slideOutCards.upsell}
					price={monthlyPriceOf(upsellModel.target)}
					/* The press is the card's claim, so it is reported on EVERY branch — there is only one
					   here, and the report sits before the dialog opens so the two steps arrive in the
					   order the funnel reads them. */
					onAction={() => {
						reportUpsellCardClaimed();
						openUpgrade({entry: "upsellCard", target: upsellModel.target});
					}}
				/>,
				slideOutHost,
			)}

			{/* The upgrade moment in the same slot (ui#2909), when no wall holds it. Its press is the card's
			    claim with the moment's wall, then the compact dialog on the tier the moment sells. */}
			{slideOutHost !== null && beside.upsell && upsellModel === null && momentModel !== null
				&& momentModel.moment !== "sendingDays" && createPortal(
					<UpgradeMomentCard
						model={momentModel}
						controls={slideOutCards.upsell}
						monthlyPriceOf={monthlyPriceOf}
						onAction={() => {
							reportUpsellCardClaimed();
							openUpgrade({entry: "upsellCard", target: momentModel.target});
						}}
						onNotNow={() => {
							slideOutCards.upsell.closeCard();
							upgradeMoment.notNow();
						}}
					/>,
					slideOutHost,
				)}

			{/* Both editor sheets are PORTALLED to body (owner report 2026-09-11, second sighting of
			    the class the success entry hit first): the reply composer hosts this panel inside the
			    download page's transformed frames, where any ancestor transform turns the sheets'
			    position: fixed into card-relative and traps them "inside the card". The home page is
			    indifferent to the portal — the sheets are viewport-anchored either way. */}
			<Portal>
			<OptionsSheet
				open={optionsOpen}
				onClose={closeEditor}
				onExited={releaseEditor}
				restoreFocusOnClose={!covered}
				returnFocus={editorReturnFocusRef.current}
				vm={vm}
				/* The footer's own Upgrade names no tier and keeps the whole ladder (owner 2026-09-17). */
				onUpgrade={() => openUpgrade({entry: "optionsFooter"})}
				/* The recipients field's inline Upgrade sells the tier its line just quoted (ui#2524). */
				onUpgradeTo={target => openUpgrade({entry: "recipientsNudge", target})}
				monthlyPriceOf={monthlyPriceOf}
			/>
			<PersonaliseSheet
				open={personaliseOpen}
				onClose={closeEditor}
				onExited={releaseEditor}
				restoreFocusOnClose={!covered}
				returnFocus={editorReturnFocusRef.current}
				vm={personalise}
				// What the miniature's files head counts and its download button promises (ui#1942).
				fileCount={files.length}
				senderChoice={senderChoice}
				forRequest={requestMode}
				requestPage={requestPage}
				onCommitted={onBuilderCommitted}
				// Called, not forwarded: the sheet wires this to `onClick`, so handing it `openLogin`
				// itself would pass the click event as that function's optional `returnFocus` element.
				// A direct `onClick={openLogin}` would not compile — it is this prop's `() => void`
				// declaration that launders the mismatch away (ui#933).
				//
				// No argument is the right call rather than a missed opportunity: signing in flips the
				// sheet out of `locked`, which unmounts both of its sign-in buttons, so there is no
				// durable element here worth naming.
				//
				// Nothing is resumed from the outcome, and that is the honest answer here rather than a
				// dropped intent (ui#1242): this CTA IS "Log in", and signing in is the whole of it. The
				// locked preview unlocks itself — `usePersonalizationDraft`'s offer effect re-runs the
				// moment `loggedIn` flips, `access` leaves "locked", and the composer the visitor was
				// already looking at becomes usable in place.
				onSignIn={() => openLogin()}
			/>
			</Portal>

			{/* Mounted HERE, at the Root level — SIBLINGS of the OptionsSheet, never inside it (ui#217).
			    Inside the sheet, dismissing the sheet would unmount the dialog mid-flow. As siblings the
			    plans dialog stacks over the still-open sheet, and the checkout dialog stacks over the
			    plans dialog on a paid CTA — the same arrangement ProfileMenu uses. Each MUI modal owns its
			    scrim; the non-modal CardSheet has none and yields Escape while that top layer is present,
			    so Escape closes only the dialog and leaves the sheet and transfer exactly as they were. */}
			{/* The limit-wall offer card's checkout (kitecorp/transferify-ui#3197), over the composer. */}
			<LimitWallDialog wall={limitWall}/>
			<FirstMonthPlansDialog
				open={claimingLimitOffer}
				moment="DOWNLOAD_CAP_REACHED"
				preselected={limitOffer.plan ?? "basic"}
				// The percentage the over-cap strip's own CTA is already showing, so a failed pricing read
				// inside the dialog can still price the tiers from the bundle's own catalogue (ui#1671).
				// Null while there is no offer, which is also every moment this dialog is closed.
				percentOff={limitOffer.offer?.percentOff ?? null}
				occasion={limitOffer.occasion}
				endsOn={limitOffer.endsOn}
				onClose={() => setClaimingLimitOffer(false)}
			/>
			<PricingDialog
				open={pricingOpen}
				onClose={() => { setPricingOpen(false); setLimitPriceShown(false); }}
				planIds={pricingPlanIds}
				currentPlanId={plan}
				onUpgrade={onUpgrade}
				host={pricingHost}
			/>
			{/* The compact single-plan dialog (ui#2529), a Root-level sibling for the same reason every
			    checkout dialog here is: it mounts an embedded Stripe sheet in place, so it has to survive
			    whatever the panel underneath does while a card number is being typed into a cross-origin
			    iframe. Rendered only once a tier has been chosen, and the tier then stays — see the state
			    above. `onSharedUpgrade` is THIS panel's own `usePricingUpgrade`, so a verdict the dialog
			    hands back (a live subscriber's prorated change, the billing portal) lands in the very
			    dialogs mounted beside it rather than in a second copy of that hook's state. */}
			{compact !== null && (
				<CompactUpgradeDialog
					open={compactOpen}
					plan={compact.plan}
					entry={compact.entry}
					reason={compact.reason}
					onClose={() => setCompactOpen(false)}
					onPaid={() => {
						setUpgradeConfirming(true);
						showSuccessToast(pricing.upgradeDialog.confirming);
					}}
					onSharedUpgrade={(target, period) => {
						// CLOSED FIRST, and this is the whole of the fix: the shared policy answers a live
						// subscriber with the plan-change confirm, which would otherwise stack on a compact
						// dialog still pitching the tier being bought — and once the change is confirmed that
						// dialog's CTA resolves to nothing at all, which is the dead button this surface
						// exists to remove. Every other verdict it takes (the portal, /account, the login
						// modal and its resumed press) leaves this dialog with nothing left to say either.
						setCompactOpen(false);
						onUpgrade(target, period);
					}}
				/>
			)}
			<CompactUpgradeDialog checkout={checkout} onClose={closeCheckout}/>
			<PlanChangeDialog {...planChange}/>
			{/* The panel's ONE referral picker (ui#1993). A Root-level sibling like every other checkout
			    dialog here: it mounts an embedded Stripe sheet, so it must survive whatever the panel
			    underneath does while a card number is being typed into a cross-origin iframe. Every
			    surface that offers the gift — the notices strip, the download-cap wall, the storage
			    refusal — opens THIS one. */}
			{referralOffer.model !== null && (
				<ReferralOfferDialog
					open={referralOffer.open}
					model={referralOffer.model}
					onClose={referralOffer.close}
					onPaid={referralOffer.closeAfterPaid}
					claim={referralClaim}
				/>
			)}
			{/* The over-cap plan dialog (ui#1454, kitecorp/transferify-ui#3800), a Root-level sibling for the same
			    reason as the dialogs above: it must survive anything the panel underneath does while a card number
			    is being typed into a cross-origin iframe. Nothing here navigates, so an abandoned payment leaves
			    the sender's selection exactly as it was and creates no transfer. */}
			<OverLimitCheckoutDialog
				open={vm.overLimit.paymentOpen}
				pricing={vm.overLimit.pricing}
				sizeLabel={vm.overLimit.sizeLabel}
				planOffer={vm.overLimit.planOffer}
				sized={vm.overLimit.sized}
				plan={vm.overLimit.plan}
				onContinuePlan={vm.overLimit.continueWithPlan}
				/* The plan's order is placed (kitecorp/transferify-ui#3229): the composer waits for the plan to switch
				   on, and every further upgrade press is answered with what happened, as the compact dialog's is. */
				onPlanPlaced={() => {
					vm.overLimit.planPlaced();
					setUpgradeConfirming(true);
					showSuccessToast(pricing.upgradeDialog.confirming);
				}}
				onClose={vm.overLimit.dismissPayment}
				onTransitionExited={() => overLimitNoticeRef.current?.focus()}
			/>
		</Root>
	);
}
